import React, { useContext, useEffect, useState } from "react";
import { Image, Modal, Pressable, ScrollView, StyleSheet, View, useWindowDimensions } from "react-native";
import Icon from "react-native-vector-icons/Ionicons";
import { FindifyDataContext, FindifyDataContextType } from "@/contexts/FindifyDataContext";
import { FindifyMenuContext } from "@/contexts/FindifyMenuContext";
import { BasePage } from "../../genericPages/helpers/BasePage";
import { FindifyFooterLogo, BottomNavigationPadding } from "../../genericPages/components/FindifyNavigation";
import { getApiFromModule } from "../../genericPages/helpers/GetContentsByModule";
import { getFeaturedImageUrl } from "../../genericPages/helpers/DetailsHelpers";
import { getTranslatedString } from "../../genericPages/helpers/FindifyTranslatedString";
import { PlaceText } from "../../genericPages/components/PlaceText";
import { PlaceColors } from "../../genericPages/helpers/PlaceColors";
import { FindifyAppHeader } from "../../genericPages/components/FindifyAppHeader";
import { useSeoTitle } from "../../genericPages/helpers/SeoTitle";
import { getContentAudioUrl } from "../../genericPages/helpers/MediaHelpers";
import { SimpleAudioPlayer } from "../../genericPages/components/SimpleAudioPlayer";

const FLOOR_SLUG_REGEX = /^(?:(.+)-)?floor-(\d+)(-accessible)?$/i;
const normalizeSlug = (value: any) => String(value ?? '').trim().toLocaleLowerCase();

const getFloorMeta = (item: any) => {
    const slug = normalizeSlug(item?.page_slug ?? item?.slug);
    const match = slug.match(FLOOR_SLUG_REGEX);
    if (!match) return null;

    return {
        slug,
        spaceSlug: match[1] || '',
        order: Number(match[2]),
        accessible: !!match[3],
    };
}

const getCategoryIdsFromRoute = (route: any) => (route?.categories ?? [])
    .map((line: any) => line?.category_id ?? line?.category?.category_id ?? line?.category?.id)
    .filter((id: any) => id !== undefined && id !== null)
    .map(Number);

const flattenVisitTree = (nodes: any[], ancestors: any[] = []): any[] => nodes.flatMap((node: any) => {
    const category = { ...node, __ancestors: ancestors };
    return [category, ...flattenVisitTree(node?.children ?? [], [...ancestors, category])];
});

const belongsToSpace = (category: any, spaceSlug: string) => !spaceSlug
    || [category, ...(category?.__ancestors ?? [])]
        .some((item: any) => normalizeSlug(item?.page_slug ?? item?.slug) === spaceSlug);

const getCategoryOrder = (category: any) => [
    ...(category?.__ancestors ?? []),
    category,
].map((item: any) => Number(item?.position ?? item?.category_id ?? 0));

const compareCategories = (a: any, b: any) => {
    const aOrder = getCategoryOrder(a);
    const bOrder = getCategoryOrder(b);
    const count = Math.max(aOrder.length, bOrder.length);
    for (let index = 0; index < count; index += 1) {
        const difference = (aOrder[index] ?? -1) - (bOrder[index] ?? -1);
        if (difference) return difference;
    }
    return Number(a?.category_id ?? 0) - Number(b?.category_id ?? 0);
}

const getCurrentRouteParams = (navigation: any) => {
    const navState = navigation?.getState?.();
    return navState?.routes?.[navState.index]?.params ?? {};
}

const getSelectedPlantsParams = (navigation: any) => {
    const params = getCurrentRouteParams(navigation);
    return {
        routeSlug: normalizeSlug(params.route_slug),
        categorySlug: normalizeSlug(params.category_slug),
    };
}

const getSelectedSpaceSlug = (navigation: any, activeMuseumSlug?: string | null) => {
    const params = getCurrentRouteParams(navigation);
    return normalizeSlug(params.route_prefix || params.space_slug || params.museum_slug || activeMuseumSlug);
}

const isAccessibleVisit = (findifyDataContext: FindifyDataContextType | null) => findifyDataContext?.visitVersion === "accessible";
const shouldIncludeRouteVariant = (item: any, selectedAccessible: boolean) => {
    if (!item?.__floorMeta) return false;
    if (selectedAccessible) return item.__floorMeta.accessible;
    return !item.__floorMeta.accessible;
}

const isEnglishLanguage = (language?: string) => language?.startsWith("en");
const localizedText = (findifyDataContext: FindifyDataContextType | null, pt: string, en: string) => {
    return isEnglishLanguage(findifyDataContext?.language) ? en : pt;
};

const getFloorTitle = (category: any, findifyDataContext: FindifyDataContextType | null) => (
    getTranslatedString(category?.name, findifyDataContext as FindifyDataContextType)
);

const getRoutePoints = (route: any) => {
    return (route?.points_of_interest ?? [])
        .slice()
        .sort((a: any, b: any) => +(a?.sequence ?? 0) - +(b?.sequence ?? 0));
}

const getPointTitle = (routePoint: any, findifyDataContext: FindifyDataContextType | null) => {
    return getTranslatedString(routePoint?.point_of_interest?.title, findifyDataContext as FindifyDataContextType)
        || getTranslatedString(routePoint?.point_of_interest?.name, findifyDataContext as FindifyDataContextType)
        || '';
}

const stripHtml = (html: string) => {
    return (html ?? '')
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<\/p>/gi, '\n')
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/\s+\n/g, '\n')
        .replace(/\n\s+/g, '\n')
        .trim();
}

const getPointSummary = (routePoint: any, findifyDataContext: FindifyDataContextType | null) => {
    const point = routePoint?.point_of_interest;
    return stripHtml(
        getTranslatedString(point?.resume, findifyDataContext as FindifyDataContextType)
        || getTranslatedString(point?.description, findifyDataContext as FindifyDataContextType)
        || ''
    );
}

const VisitNavigation = ({
    route,
    currentIndex,
    routeStarted,
    nextFloorTitle,
    findifyDataContext,
    onPrevious,
    onNext,
}: {
    route: any,
    currentIndex: number,
    routeStarted: boolean,
    nextFloorTitle?: string,
    findifyDataContext: FindifyDataContextType | null,
    onPrevious: () => void,
    onNext: () => void,
}) => {
    const colors = PlaceColors(findifyDataContext);
    const points = getRoutePoints(route);
    if (points.length === 0) return null;

    const currentPoint = points[currentIndex];
    const previousPoint = routeStarted ? points[currentIndex - 1] : undefined;
    const nextPoint = routeStarted ? points[currentIndex + 1] : currentPoint;
    const shouldContinueToNextFloor = routeStarted && !!nextFloorTitle && !nextPoint;
    const nextButtonDisabled = !nextPoint && !shouldContinueToNextFloor;

    return <View style={styles.visitNavigation}>
        <Pressable
            disabled={!previousPoint}
            style={[styles.visitNavButton, { borderColor: colors.primary.withAlpha(36) }, !previousPoint ? styles.visitNavDisabled : null]}
            onPress={onPrevious}>
            <Icon name="arrow-back-outline" size={23} color={previousPoint ? colors.primary.get() : colors.primary.withAlpha(70)} />
            <View style={styles.visitNavTextBlock}>
                <PlaceText style={[styles.visitNavLabel, { color: colors.primary.get() }, !previousPoint ? { color: colors.primary.withAlpha(90) } : null]}>{localizedText(findifyDataContext, "Sala anterior", "Previous room")}</PlaceText>
                <PlaceText numberOfLines={2} style={[styles.visitNavTitle, { color: colors.text.get() }, !previousPoint ? { color: colors.text.withAlpha(110) } : null]}>
                    {previousPoint ? getPointTitle(previousPoint, findifyDataContext) : localizedText(findifyDataContext, "Início", "Start", "Début")}
                </PlaceText>
            </View>
        </Pressable>
        <Pressable
            disabled={nextButtonDisabled}
            style={[styles.visitNavButton, styles.visitNavButtonNext, { backgroundColor: colors.primary.get(), borderColor: colors.primary.get() }, nextButtonDisabled ? styles.visitNavDisabled : null]}
            onPress={onNext}>
            <View style={styles.visitNavTextBlock}>
                <PlaceText style={styles.visitNavLabelNext}>{routeStarted ? localizedText(findifyDataContext, "Sala seguinte", "Next room") : localizedText(findifyDataContext, "Percurso", "Route", "Parcours")}</PlaceText>
                <PlaceText numberOfLines={2} style={styles.visitNavTitleNext}>
                    {routeStarted ? (nextPoint ? getPointTitle(nextPoint, findifyDataContext) : shouldContinueToNextFloor ? `${localizedText(findifyDataContext, "Continuar para", "Continue to", "Continuer vers")} ${nextFloorTitle}` : localizedText(findifyDataContext, "Fim", "End", "Fin")) : localizedText(findifyDataContext, "Começar visita", "Start visit", "Commencer la visite")}
                </PlaceText>
            </View>
            <Icon name="arrow-forward-outline" size={25} color="#fff" />
        </Pressable>
    </View>
}

const FloorCard = ({
    category,
    route,
    currentIndex,
    routeStarted,
    showMap,
    activeRouteId,
    routeId,
    nextFloorTitle,
    findifyDataContext,
    onPreviousPoint,
    onNextPoint,
    onOpenPoint,
    onToggleMap,
    isTablet,
    isLandscapeTablet,
}: {
    category: any,
    route: any,
    currentIndex: number,
    routeStarted: boolean,
    showMap: boolean,
    activeRouteId: number,
    routeId: number,
    nextFloorTitle?: string,
    findifyDataContext: FindifyDataContextType | null,
    onPreviousPoint: (routeId: number) => void,
    onNextPoint: (routeId: number) => void,
    onOpenPoint: (routePoint: any) => void,
    onToggleMap: (routeId: number) => void,
    isTablet?: boolean,
    isLandscapeTablet?: boolean,
}) => {
    const colors = PlaceColors(findifyDataContext);
    const [zoomOpen, setZoomOpen] = useState(false);
    const points = getRoutePoints(route);
    const currentPoint = points[currentIndex];
    const floorImageUrl = getFeaturedImageUrl(category);
    const pointImageUrl = getFeaturedImageUrl(currentPoint?.point_of_interest);
    const shouldShowMap = !routeStarted || showMap || !pointImageUrl;
    const imageUrl = shouldShowMap ? floorImageUrl : pointImageUrl;
    const point = currentPoint?.point_of_interest;
    const pointTitle = getPointTitle(currentPoint, findifyDataContext);
    const pointSummary = getPointSummary(currentPoint, findifyDataContext);
    const audioUrl = getContentAudioUrl(point);

    return <View style={styles.floorSection}>
        <VisitNavigation
            route={route}
            currentIndex={currentIndex}
            routeStarted={routeStarted}
            nextFloorTitle={nextFloorTitle}
            findifyDataContext={findifyDataContext}
            onPrevious={() => onPreviousPoint(routeId)}
            onNext={() => onNextPoint(routeId)}
        />
        <Pressable
            style={[
                styles.floorImageCard,
                isTablet ? styles.floorImageCardTablet : null,
                isLandscapeTablet ? styles.floorImageCardLandscapeTablet : null,
                { borderColor: colors.primary.withAlpha(45), backgroundColor: colors.background.solidAlphaWhiteBase(18) }
            ]}
            onPress={() => {
                if (shouldShowMap) {
                    setZoomOpen(true);
                    return;
                }
                onOpenPoint(currentPoint);
            }}>
            {imageUrl ? (
                <Image
                    source={{ uri: imageUrl }}
                    resizeMode={shouldShowMap ? "contain" : "cover"}
                    style={[
                        styles.floorImage,
                        isTablet ? styles.floorImageTablet : null,
                        isLandscapeTablet ? styles.floorImageLandscapeTablet : null,
                    ]}
                />
            ) : null}
            {routeStarted && pointImageUrl ? (
                <Pressable style={[styles.mapOverlayButton, { backgroundColor: colors.primary.withAlpha(235) }]} onPress={() => onToggleMap(routeId)}>
                    <Icon name={shouldShowMap ? "image-outline" : "map-outline"} size={18} color="#fff" />
                    <PlaceText style={styles.mapOverlayText}>{shouldShowMap ? localizedText(findifyDataContext, "Foto", "Photo", "Photo") : localizedText(findifyDataContext, "Mapa", "Map", "Plan")}</PlaceText>
                </Pressable>
            ) : null}
        </Pressable>
        <View style={[styles.pointInfoCard, { backgroundColor: colors.background.solidAlphaWhiteBase(68), borderColor: colors.primary.withAlpha(25) }]}>
            <View style={styles.pointInfoHeader}>
                <PlaceText style={[styles.pointInfoEyebrow, { color: colors.primary.get() }]}>
                    {routeStarted ? localizedText(findifyDataContext, "LOCALIZAÇÃO ATUAL", "CURRENT LOCATION", "EMPLACEMENT ACTUEL") : localizedText(findifyDataContext, "PLANTA DO PISO", "FLOOR MAP", "PLAN DE L'ÉTAGE")}
                </PlaceText>
                {routeStarted && point ? (
                    <Pressable style={styles.pointInfoOpenButton} onPress={() => onOpenPoint(currentPoint)}>
                        <PlaceText style={[styles.pointInfoOpenText, { color: colors.primary.get() }]}>{localizedText(findifyDataContext, "Abrir detalhe", "Open details", "Ouvrir les détails")}</PlaceText>
                        <Icon name="open-outline" size={17} color={colors.primary.get()} />
                    </Pressable>
                ) : null}
            </View>
            <PlaceText style={[styles.pointInfoTitle, { color: colors.primary.get() }]}>
                {routeStarted && pointTitle ? pointTitle : getFloorTitle(category, findifyDataContext)}
            </PlaceText>
            {routeStarted ? <SimpleAudioPlayer audioUrl={audioUrl} findifyDataContext={findifyDataContext} title={localizedText(findifyDataContext, "Audioguia", "Audio Guide", "Audioguide")} /> : null}
            {!!pointSummary && routeStarted ? (
                <PlaceText numberOfLines={5} style={[styles.pointInfoSummary, { color: colors.text.get() }]}>{pointSummary}</PlaceText>
            ) : (
                <PlaceText style={[styles.pointInfoSummary, { color: colors.text.withAlpha(175) }]}>
                    {localizedText(findifyDataContext, "Use os botões acima para iniciar e seguir o percurso recomendado.", "Use the buttons above to start and follow the recommended route.", "Utilisez les boutons ci-dessus pour commencer et suivre le parcours recommandé.")}
                </PlaceText>
            )}
        </View>
        <Modal visible={zoomOpen} transparent={true} animationType="fade" onRequestClose={() => setZoomOpen(false)}>
            <View style={styles.zoomBackdrop}>
                <Pressable style={styles.zoomClose} onPress={() => setZoomOpen(false)}>
                    <Icon name="close-outline" size={34} color="#fff" />
                </Pressable>
                <ScrollView
                    horizontal
                    maximumZoomScale={4}
                    minimumZoomScale={1}
                    showsHorizontalScrollIndicator={false}
                    showsVerticalScrollIndicator={false}
                    contentContainerStyle={styles.zoomScrollContent}>
                    <ScrollView
                        maximumZoomScale={4}
                        minimumZoomScale={1}
                        showsHorizontalScrollIndicator={false}
                        showsVerticalScrollIndicator={false}
                        contentContainerStyle={styles.zoomScrollContent}>
                        {imageUrl ? <Image source={{ uri: imageUrl }} resizeMode="contain" style={styles.zoomImage} /> : null}
                    </ScrollView>
                </ScrollView>
            </View>
        </Modal>
    </View>
}

const FloorSelector = ({
    floorCategories,
    activeCategoryId,
    findifyDataContext,
    onSelectFloor,
}: {
    floorCategories: any[],
    activeCategoryId?: number,
    findifyDataContext: FindifyDataContextType | null,
    onSelectFloor: (categoryId: number) => void,
}) => {
    const colors = PlaceColors(findifyDataContext);
    const [open, setOpen] = useState(false);
    const activeCategory = floorCategories.find((category: any) => category.category_id === activeCategoryId);

    return <View style={styles.floorSelectorWrap}>
        <Pressable
            style={[styles.floorSelectBox, { borderColor: colors.primary.withAlpha(36), backgroundColor: colors.background.solidAlphaWhiteBase(60) }]}
            onPress={() => setOpen(true)}>
            <PlaceText style={[styles.floorSelectValue, { color: colors.primary.get() }]}>
                {getFloorTitle(activeCategory, findifyDataContext) || localizedText(findifyDataContext, "Escolher piso", "Choose floor")}
            </PlaceText>
            <Icon name="chevron-down-outline" size={22} color={colors.primary.get()} />
        </Pressable>
        <Modal visible={open} transparent={true} animationType="fade" onRequestClose={() => setOpen(false)}>
            <Pressable style={styles.floorSelectBackdrop} onPress={() => setOpen(false)}>
                <View style={[styles.floorSelectModal, { backgroundColor: colors.background.get(), borderColor: colors.primary.withAlpha(35) }]}>
                    <View style={styles.floorSelectHandle} />
                    <PlaceText style={[styles.floorSelectTitle, { color: colors.primary.get() }]}>{localizedText(findifyDataContext, "Escolher piso", "Choose floor")}</PlaceText>
                    {floorCategories.map((category: any) => {
                        const isActive = category.category_id === activeCategoryId;
                        return <Pressable
                            key={"floor_selector_" + category.category_id}
                            style={[styles.floorSelectOption, { borderColor: colors.primary.withAlpha(25) }, isActive ? { backgroundColor: colors.primary.get() } : null]}
                            onPress={() => {
                                onSelectFloor(category.category_id);
                                setOpen(false);
                            }}>
                            <PlaceText style={[styles.floorSelectOptionText, { color: colors.primary.get() }, isActive ? styles.floorSelectOptionTextActive : null]}>
                                {getFloorTitle(category, findifyDataContext)}
                            </PlaceText>
                            {isActive ? <Icon name="checkmark-outline" size={22} color="#fff" /> : null}
                        </Pressable>
                    })}
                </View>
            </Pressable>
        </Modal>
    </View>
}

export const PlantsPage = ({ navigation }: any) => {
    const findifyDataContext = useContext(FindifyDataContext);
    const findifyMenuContext = useContext(FindifyMenuContext);
    useSeoTitle(localizedText(findifyDataContext, "Visita Guiada", "Guided Visit"), 'Rota de Museus de Caminha');
    const { width, height } = useWindowDimensions();
    const isTablet = width >= 768;
    const isLandscapeTablet = isTablet && width > height;
    const colors = PlaceColors(findifyDataContext);
    const [floorCategories, setFloorCategories] = useState<any[]>([]);
    const [routesById, setRoutesById] = useState<Record<number, any>>({});
    const [routeIdByFloorCategory, setRouteIdByFloorCategory] = useState<Record<number, number>>({});
    const [floorCategoryByRouteId, setFloorCategoryByRouteId] = useState<Record<number, number>>({});
    const [routeIds, setRouteIds] = useState<number[]>([]);
    const [currentPointIndexes, setCurrentPointIndexes] = useState<Record<number, number>>({});
    const [routeStartedById, setRouteStartedById] = useState<Record<number, boolean>>({});
    const [showMapByRouteId, setShowMapByRouteId] = useState<Record<number, boolean>>({});
    const [activeRouteId, setActiveRouteId] = useState<number | undefined>();

    useEffect(() => {
        let isMounted = true;
        const selectedAccessible = Number(findifyDataContext?.workspace?.workspace_id) === 159
            ? false
            : isAccessibleVisit(findifyDataContext);
        const selectedSpaceSlug = getSelectedSpaceSlug(navigation, findifyMenuContext?.activeMuseumSlug);
        const selectedPlantsParams = getSelectedPlantsParams(navigation);
        const categoriesApi = getApiFromModule("categories", findifyDataContext as FindifyDataContextType);
        const routesApi = getApiFromModule("routes", findifyDataContext as FindifyDataContextType);

        Promise.all([
            categoriesApi?.getVisitTree(null, { category_type: "points_of_interest", active: 1 }),
            routesApi?.getList(0, 100, null, {}, null),
        ]).then(async ([categoriesRes, routesRes]: any[]) => {
            const visitCategories = flattenVisitTree(categoriesRes?.results ?? [])
                .map((category: any) => ({ ...category, __floorMeta: getFloorMeta(category) }));
            const routeSummaries = (routesRes?.results ?? [])
                .map((route: any) => ({ ...route, __floorMeta: getFloorMeta(route) }));

            // The tree is the source of truth. Slugs remain only as a fallback
            // for older workspaces which have not yet assigned route categories.
            const routeCategoryIds = new Set(routeSummaries.flatMap(getCategoryIdsFromRoute));
            const categoriesWithRoutes = visitCategories.filter((category: any) =>
                routeCategoryIds.has(Number(category.category_id))
                || routeSummaries.some((route: any) => normalizeSlug(route.page_slug) === normalizeSlug(category.page_slug))
            );
            const availableSpaceSlugs = Array.from(new Set(categoriesWithRoutes
                .flatMap((category: any) => [category, ...(category.__ancestors ?? [])])
                .map((category: any) => normalizeSlug(category.page_slug ?? category.slug))
                .filter(Boolean)));
            const effectiveSpaceSlug = selectedSpaceSlug && availableSpaceSlugs.includes(selectedSpaceSlug)
                ? selectedSpaceSlug
                : '';
            const categories = categoriesWithRoutes
                .filter((category: any) => belongsToSpace(category, effectiveSpaceSlug))
                .filter((category: any) => !category.__floorMeta || shouldIncludeRouteVariant(category, selectedAccessible))
                .sort(compareCategories);

            const filteredRouteSummaries = routeSummaries
                .filter((route: any) => {
                    const matchingCategories = categories.filter((category: any) =>
                        getCategoryIdsFromRoute(route).includes(Number(category.category_id))
                        || normalizeSlug(route.page_slug) === normalizeSlug(category.page_slug)
                    );
                    return matchingCategories.length > 0;
                })
                .sort((a: any, b: any) => {
                    const aCategory = categories.find((category: any) => getCategoryIdsFromRoute(a).includes(Number(category.category_id)) || normalizeSlug(a.page_slug) === normalizeSlug(category.page_slug));
                    const bCategory = categories.find((category: any) => getCategoryIdsFromRoute(b).includes(Number(category.category_id)) || normalizeSlug(b.page_slug) === normalizeSlug(category.page_slug));
                    return aCategory && bCategory ? compareCategories(aCategory, bCategory) : 0;
                });

            const routeDetails = await Promise.all(filteredRouteSummaries.map((route: any) => {
                const routeLookup = route?.route_id ?? route?.page_slug;
                return routesApi?.get(routeLookup, null, null)
                    .then((details: any) => ({ ...details, __floorMeta: route.__floorMeta, __summary: route }))
                    .catch(() => null);
            }));

            if (!isMounted) return;

            const nextRouteIdByFloorCategory: Record<number, number> = {};
            const nextFloorCategoryByRouteId: Record<number, number> = {};
            const nextRouteIds: number[] = [];
            const nextCategories = categories.filter((category: any) => {
                const route = routeDetails.filter(Boolean).find((item: any) => {
                    const summary = item.__summary ?? item;
                    return getCategoryIdsFromRoute(summary).includes(Number(category.category_id))
                        || normalizeSlug(summary.page_slug) === normalizeSlug(category.page_slug);
                });
                if (!route?.route_id) return false;
                nextRouteIdByFloorCategory[category.category_id] = route.route_id;
                nextFloorCategoryByRouteId[route.route_id] = category.category_id;
                nextRouteIds.push(route.route_id);
                return true;
            });

            const nextRoutes: Record<number, any> = {};
            routeDetails.filter(Boolean).forEach((route: any) => {
                nextRoutes[route.route_id] = route;
            });

            setFloorCategories(nextCategories);
            setRoutesById(nextRoutes);
            setRouteIdByFloorCategory(nextRouteIdByFloorCategory);
            setFloorCategoryByRouteId(nextFloorCategoryByRouteId);
            setRouteIds(nextRouteIds);
            const selectedRoute = selectedPlantsParams.routeSlug
                ? routeDetails.filter(Boolean).find((route: any) => {
                    const summary = route.__summary ?? route;
                    return [route.page_slug, route.slug, summary.page_slug, summary.slug]
                        .map(normalizeSlug)
                        .includes(selectedPlantsParams.routeSlug);
                })
                : null;
            const selectedCategory = selectedPlantsParams.categorySlug
                ? nextCategories.find((category: any) => normalizeSlug(category.page_slug ?? category.slug) === selectedPlantsParams.categorySlug)
                : null;
            const initialRouteId = selectedRoute?.route_id
                ?? (selectedCategory ? nextRouteIdByFloorCategory[selectedCategory.category_id] : undefined)
                ?? nextRouteIds[0];
            setActiveRouteId(initialRouteId);
            setRouteStartedById({});
            setShowMapByRouteId({});
            setCurrentPointIndexes((current) => {
                const next = { ...current };
                nextRouteIds.forEach((routeId) => {
                    next[routeId] = next[routeId] ?? 0;
                });
                return next;
            });
        }).catch((error: any) => {
            console.error("Failed to load plants floors/routes:", error);
        });

        return () => {
            isMounted = false;
        }
    }, [findifyDataContext?.workspace?.workspace_id, findifyDataContext?.visitVersion, findifyMenuContext?.activeMuseumSlug]);

    const activeCategoryId = activeRouteId ? floorCategoryByRouteId[activeRouteId] : undefined;
    const activeCategory = floorCategories.find((category: any) => category.category_id === activeCategoryId);

    const changeRoutePoint = (routeId: number, direction: -1 | 1) => {
        const points = getRoutePoints(routesById[routeId]);
        setActiveRouteId(routeId);

        if (direction === 1 && !routeStartedById[routeId]) {
            setRouteStartedById((current) => ({
                ...current,
                [routeId]: true,
            }));
            setShowMapByRouteId((current) => ({
                ...current,
                [routeId]: false,
            }));
            return;
        }

        setCurrentPointIndexes((current) => {
            const currentIndex = current[routeId] ?? 0;
            if (direction === -1 && currentIndex === 0) {
                setRouteStartedById((started) => ({
                    ...started,
                    [routeId]: false,
                }));
                setShowMapByRouteId((maps) => ({
                    ...maps,
                    [routeId]: false,
                }));
                return current;
            }

            const currentRouteOrderIndex = routeIds.indexOf(routeId);
            const nextRouteId = routeIds[currentRouteOrderIndex + 1];
            if (direction === 1 && currentIndex >= points.length - 1 && nextRouteId) {
                setActiveRouteId(nextRouteId);
                setRouteStartedById((started) => ({
                    ...started,
                    [nextRouteId]: false,
                }));
                setShowMapByRouteId((maps) => ({
                    ...maps,
                    [nextRouteId]: false,
                }));
                return {
                    ...current,
                    [nextRouteId]: current[nextRouteId] ?? 0,
                };
            }

            const nextIndex = Math.max(0, Math.min(points.length - 1, currentIndex + direction));
            return {
                ...current,
                [routeId]: nextIndex,
            };
        });
    }

    const openRoutePointDetails = (routePoint: any) => {
        const point = routePoint?.point_of_interest;
        if (!point?.point_of_interest_id) return;
        navigation.push("PlaceContentDetails", {
            module: "points_of_interest",
            content_id: point.point_of_interest_id,
            page_slug: point.page_slug,
        });
    }

    const titleSection = <View style={[styles.titleBlock, isLandscapeTablet ? styles.titleBlockLandscapeTablet : null]}>
        <PlaceText style={[styles.title, { color: colors.primary.get() }]}>{localizedText(findifyDataContext, "Visita Guiada", "Guided Visit")}</PlaceText>
        <PlaceText style={[styles.subtitle, { color: colors.text.withAlpha(210) }]}>{localizedText(findifyDataContext, "Percurso recomendado", "Recommended route")}</PlaceText>
    </View>;

    const floorSelectorSection = <FloorSelector
        floorCategories={floorCategories}
        activeCategoryId={activeCategoryId}
        findifyDataContext={findifyDataContext}
        onSelectFloor={(categoryId: number) => {
            const routeId = routeIdByFloorCategory[categoryId];
            if (routeId) setActiveRouteId(routeId);
        }}
    />;

    const activeCategoryRouteId = activeCategory ? routeIdByFloorCategory[activeCategory.category_id] : undefined;
    const activeRouteIndex = activeCategoryRouteId ? routeIds.indexOf(activeCategoryRouteId) : -1;
    const nextRouteId = activeRouteIndex >= 0 ? routeIds[activeRouteIndex + 1] : undefined;
    const nextCategoryId = nextRouteId ? floorCategoryByRouteId[nextRouteId] : undefined;
    const nextCategory = floorCategories.find((category: any) => category.category_id === nextCategoryId);
    const nextFloorTitle = nextCategory ? getFloorTitle(nextCategory, findifyDataContext) : undefined;

    const floorCardSection = activeCategory && activeCategoryRouteId ? (
        <View style={isLandscapeTablet ? styles.tabletLandscapeMapWrap : undefined}>
            <FloorCard
                key={activeCategory.category_id}
                category={activeCategory}
                route={routesById[activeCategoryRouteId]}
                currentIndex={currentPointIndexes[activeCategoryRouteId] ?? 0}
                routeStarted={routeStartedById[activeCategoryRouteId] ?? false}
                showMap={showMapByRouteId[activeCategoryRouteId] ?? false}
                activeRouteId={activeRouteId ?? activeCategoryRouteId}
                routeId={activeCategoryRouteId}
                nextFloorTitle={nextFloorTitle}
                findifyDataContext={findifyDataContext}
                onPreviousPoint={(routeId: number) => changeRoutePoint(routeId, -1)}
                onNextPoint={(routeId: number) => changeRoutePoint(routeId, 1)}
                onOpenPoint={openRoutePointDetails}
                onToggleMap={(routeId: number) => setShowMapByRouteId((current) => ({
                    ...current,
                    [routeId]: !current[routeId],
                }))}
                isTablet={isTablet}
                isLandscapeTablet={isLandscapeTablet}
            />
        </View>
    ) : null;

    return <BasePage navigation={navigation}>
        <View style={[styles.page, { backgroundColor: colors.background.get() }]}>
            <FindifyAppHeader findifyDataContext={findifyDataContext} navigation={navigation} />
            <ScrollView
                bounces={false}
                showsVerticalScrollIndicator={false}
                contentContainerStyle={[
                    styles.content,
                    {
                        maxWidth: isLandscapeTablet ? 1180 : isTablet ? 820 : 960,
                        paddingHorizontal: isTablet ? 40 : 24,
                    }
                ]}>
                {isLandscapeTablet ? (
                    <View style={styles.tabletLandscapeLayout}>
                        <View style={styles.tabletLandscapeSidebar}>
                            {titleSection}
                            {floorSelectorSection}
                        </View>
                        <View style={styles.tabletLandscapeMapPane}>
                            {floorCardSection}
                        </View>
                    </View>
                ) : (
                    <>
                        {titleSection}
                        {floorSelectorSection}
                        {floorCardSection}
                    </>
                )}
                <FindifyFooterLogo findifyDataContext={findifyDataContext} />
            </ScrollView>
        </View>
    </BasePage>
}

const styles = StyleSheet.create({
    page: {
        flex: 1,
        backgroundColor: "#f9f7f2",
    },
    content: {
        paddingTop: 22,
        paddingBottom: BottomNavigationPadding + 42,
        width: "100%" as any,
        alignSelf: "center",
    },
    tabletLandscapeMapWrap: {
        width: '100%',
        alignSelf: 'center',
    },
    tabletLandscapeLayout: {
        width: "100%" as any,
        flexDirection: "row",
        alignItems: "flex-start",
        gap: 28,
    },
    tabletLandscapeSidebar: {
        width: 340,
        gap: 0,
    },
    tabletLandscapeMapPane: {
        flex: 1,
        minWidth: 0,
    },
    titleBlock: {
        marginBottom: 16,
    },
    titleBlockLandscapeTablet: {
        marginBottom: 22,
    },
    title: {
        color: "#990000",
        fontFamily: "Georgia",
        fontSize: 32,
        lineHeight: 40,
        fontWeight: "700",
    },
    subtitle: {
        color: "#3a2a28",
        fontSize: 18,
        lineHeight: 28,
        marginTop: 4,
    },
    currentLocationBox: {
        borderWidth: 1,
        borderColor: "rgba(153, 0, 0, 0.28)",
        borderRadius: 8,
        paddingHorizontal: 10,
        paddingVertical: 10,
        marginBottom: 16,
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 10,
    },
    currentLocationBoxLandscapeTablet: {
        flexDirection: "column",
        alignItems: "stretch",
        paddingHorizontal: 14,
        paddingVertical: 14,
        marginBottom: 18,
    },
    currentLocationInfo: {
        flex: 1,
        minWidth: 0,
    },
    roomPill: {
        alignSelf: "flex-start",
        backgroundColor: "#f5c443",
        borderRadius: 14,
        paddingHorizontal: 12,
        paddingVertical: 4,
        marginBottom: 7,
        shadowOpacity: 0.22,
        shadowRadius: 10,
        shadowOffset: { width: 0, height: 0 },
    },
    roomPillText: {
        color: "#6b4a10",
        fontSize: 14,
        lineHeight: 20,
        fontWeight: "700",
    },
    currentLocationText: {
        color: "#990000",
        fontSize: 14,
        lineHeight: 20,
        fontWeight: "700",
        letterSpacing: 2,
    },
    currentNavActions: {
        flexDirection: "row",
        alignItems: "center",
        gap: 4,
        borderLeftWidth: 1,
        paddingLeft: 8,
    },
    currentNavActionsLandscapeTablet: {
        borderLeftWidth: 0,
        borderTopWidth: 1,
        paddingLeft: 0,
        paddingTop: 10,
        justifyContent: "space-between",
    },
    currentNavButton: {
        minWidth: 52,
        minHeight: 44,
        alignItems: "center",
        justifyContent: "center",
        paddingHorizontal: 4,
        paddingVertical: 2,
        borderRadius: 6,
    },
    currentNavButtonLandscapeTablet: {
        flex: 1,
        minHeight: 52,
        backgroundColor: "rgba(255,255,255,0.44)",
    },
    currentNavText: {
        marginTop: 1,
        fontSize: 8,
        lineHeight: 10,
        fontWeight: "700",
        letterSpacing: 0.2,
        textTransform: "uppercase",
        textAlign: "center",
    },
    floorSelectorWrap: {
        alignItems: "stretch",
        marginBottom: 20,
        width: "100%" as any,
    },
    floorSelectBox: {
        minHeight: 54,
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        borderWidth: 1,
        borderRadius: 8,
        paddingHorizontal: 16,
        paddingVertical: 10,
        shadowColor: "#000",
        shadowOpacity: 0.07,
        shadowRadius: 6,
        shadowOffset: { width: 0, height: 2 },
    },
    floorSelectValue: {
        fontSize: 18,
        lineHeight: 24,
        fontWeight: "700",
    },
    floorSelectBackdrop: {
        flex: 1,
        backgroundColor: "rgba(0,0,0,0.42)",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
    },
    floorSelectModal: {
        width: "100%" as any,
        maxWidth: 420,
        borderWidth: 1,
        borderRadius: 12,
        padding: 18,
        shadowColor: "#000",
        shadowOpacity: 0.2,
        shadowRadius: 16,
        shadowOffset: { width: 0, height: 8 },
    },
    floorSelectHandle: {
        width: 46,
        height: 4,
        borderRadius: 2,
        alignSelf: "center",
        backgroundColor: "rgba(153,0,0,0.22)",
        marginBottom: 14,
    },
    floorSelectTitle: {
        fontFamily: "Georgia",
        fontSize: 24,
        lineHeight: 30,
        fontWeight: "700",
        marginBottom: 14,
    },
    floorSelectOption: {
        minHeight: 54,
        borderWidth: 1,
        borderRadius: 8,
        paddingHorizontal: 14,
        paddingVertical: 10,
        marginBottom: 10,
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        backgroundColor: "rgba(255,255,255,0.55)",
    },
    floorSelectOptionText: {
        fontSize: 17,
        lineHeight: 23,
        fontWeight: "700",
    },
    floorSelectOptionTextActive: {
        color: "#fff",
    },
    floorSection: {
        marginTop: 0,
        marginBottom: 34,
    },
    floorTitleRow: {
        flexDirection: "row",
        alignItems: "center",
        marginBottom: 14,
        gap: 16,
    },
    floorTitleLine: {
        flex: 1,
        height: 1,
        backgroundColor: "rgba(226, 177, 60, 0.35)",
    },
    floorTitle: {
        color: "#990000",
        fontFamily: "Georgia",
        fontSize: 26,
        lineHeight: 34,
        fontWeight: "700",
        textAlign: "center",
    },
    floorImageCard: {
        minHeight: 360,
        borderWidth: 1,
        borderColor: "rgba(153, 0, 0, 0.24)",
        borderRadius: 8,
        backgroundColor: "#fff",
        shadowColor: "#000",
        shadowOpacity: 0.12,
        shadowRadius: 10,
        shadowOffset: { width: 0, height: 3 },
        alignItems: "center",
        justifyContent: "center",
        padding: 8,
        overflow: "hidden",
        marginBottom: 14,
    },
    floorImageCardTablet: {
        minHeight: 520,
    },
    floorImageCardLandscapeTablet: {
        minHeight: 500,
    },
    floorImage: {
        width: "100%",
        height: 344,
    },
    floorImageTablet: {
        height: 504,
    },
    floorImageLandscapeTablet: {
        height: 484,
    },
    mapOverlayButton: {
        position: "absolute",
        top: 14,
        right: 14,
        minWidth: 74,
        height: 40,
        borderRadius: 20,
        backgroundColor: "rgba(153, 0, 0, 0.92)",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: 6,
        paddingHorizontal: 12,
        shadowColor: "#000",
        shadowOpacity: 0.22,
        shadowRadius: 7,
        shadowOffset: { width: 0, height: 3 },
        zIndex: 2,
        backdropFilter: "blur(8px)" as any,
    },
    mapOverlayText: {
        color: "#fff",
        fontSize: 11,
        lineHeight: 14,
        fontWeight: "700",
        letterSpacing: 1,
        textTransform: "uppercase",
    },
    visitNavigation: {
        flexDirection: "row",
        gap: 10,
        marginBottom: 14,
    },
    visitNavButton: {
        flex: 1,
        minHeight: 74,
        borderRadius: 8,
        borderWidth: 1,
        backgroundColor: "rgba(255,255,255,0.72)",
        paddingHorizontal: 12,
        paddingVertical: 10,
        flexDirection: "row",
        alignItems: "center",
        gap: 8,
        shadowColor: "#000",
        shadowOpacity: 0.08,
        shadowRadius: 8,
        shadowOffset: { width: 0, height: 2 },
    },
    visitNavButtonNext: {
        justifyContent: "space-between",
    },
    visitNavDisabled: {
        opacity: 0.55,
    },
    visitNavTextBlock: {
        flex: 1,
        minWidth: 0,
    },
    visitNavLabel: {
        fontSize: 11,
        lineHeight: 15,
        fontWeight: "700",
        textTransform: "uppercase",
        letterSpacing: 1.1,
    },
    visitNavTitle: {
        marginTop: 4,
        fontSize: 14,
        lineHeight: 18,
        fontWeight: "700",
    },
    visitNavLabelNext: {
        color: "rgba(255,255,255,0.75)",
        fontSize: 11,
        lineHeight: 15,
        fontWeight: "700",
        textTransform: "uppercase",
        letterSpacing: 1.1,
    },
    visitNavTitleNext: {
        color: "#fff",
        marginTop: 4,
        fontSize: 14,
        lineHeight: 18,
        fontWeight: "700",
    },
    pointInfoCard: {
        borderWidth: 1,
        borderRadius: 8,
        padding: 16,
        marginBottom: 22,
        shadowColor: "#000",
        shadowOpacity: 0.06,
        shadowRadius: 8,
        shadowOffset: { width: 0, height: 2 },
    },
    pointInfoHeader: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 10,
        marginBottom: 8,
    },
    pointInfoEyebrow: {
        fontSize: 12,
        lineHeight: 16,
        fontWeight: "700",
        letterSpacing: 1.8,
    },
    pointInfoOpenButton: {
        minHeight: 32,
        flexDirection: "row",
        alignItems: "center",
        gap: 4,
    },
    pointInfoOpenText: {
        fontSize: 12,
        lineHeight: 16,
        fontWeight: "700",
    },
    pointInfoTitle: {
        fontFamily: "Georgia",
        fontSize: 26,
        lineHeight: 32,
        fontWeight: "700",
        marginBottom: 8,
    },
    pointInfoSummary: {
        fontSize: 16,
        lineHeight: 25,
    },
    zoomBackdrop: {
        flex: 1,
        backgroundColor: "rgba(0, 0, 0, 0.92)",
        alignItems: "center",
        justifyContent: "center",
    },
    zoomClose: {
        position: "absolute",
        top: 18,
        right: 18,
        zIndex: 2,
        width: 48,
        height: 48,
        borderRadius: 24,
        backgroundColor: "rgba(153, 0, 0, 0.9)",
        alignItems: "center",
        justifyContent: "center",
    },
    zoomScrollContent: {
        minWidth: "100%" as any,
        minHeight: "100%" as any,
        alignItems: "center",
        justifyContent: "center",
    },
    zoomImage: {
        width: "100vw" as any,
        height: "90vh" as any,
    },
});
